import React, { useState, useEffect } from 'react';
import {
    Container, Box, Typography, Button, Paper, Alert,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Chip, IconButton, Tooltip, Stack, Grid, Card, CardContent,
    CircularProgress
} from '@mui/material';
import {
    Flight as FlightIcon,
    Edit as EditIcon,
    Add as AddIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    Schedule as ScheduleIcon,
    Delete as DeleteIcon,
    ContentCopy as CopyIcon // Unused - duplicate feature removed
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getClientPrincipal } from '../services/auth';
import { deleteForm, duplicateForm, extractUserEmail, TravelFormServer } from '../services/travel';
import { getDabUrl } from '../services/api';

const getStatusColor = (status: string) => {
    switch (status) {
        case 'approved': return 'success';
        case 'submitted': return 'info';
        case 'draft': return 'warning';
        default: return 'default';
    }
};

const getStatusIcon = (status: string) => {
    switch (status) {
        case 'approved': return <CheckCircleIcon fontSize="small" />;
        case 'submitted': return <ScheduleIcon fontSize="small" />;
        case 'draft': return <WarningIcon fontSize="small" />;
        default: return <ScheduleIcon fontSize="small" />;
    }
};

const TravelListPage: React.FC = () => {
    const [travelForms, setTravelForms] = useState<TravelFormServer[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const navigate = useNavigate();

    // Load travel forms from API
    useEffect(() => {
        loadTravelForms();
    }, []);

    const loadTravelForms = async () => {
        try {
            setLoading(true);
            const principal = await getClientPrincipal();
            if (!principal || !principal.userId) {
                setError('Not authenticated');
                return;
            }

            const userEmail = extractUserEmail(principal).toLowerCase();

            // Fetch all travel forms
            const res = await fetch(getDabUrl('TravelForms'), {
                credentials: 'include'
            });
            if (!res.ok) throw new Error('Failed to fetch travel forms');

            const data = await res.json();
            const allForms: TravelFormServer[] = data.value || data || [];

            // Filter to only show the logged-in user's forms
            const forms = allForms.filter(f =>
                (f.owner_user_id || '').toLowerCase() === userEmail ||
                (f.owner_email || '').toLowerCase() === userEmail
            );

            setTravelForms(forms);
            setError(null);
        } catch (e) {
            console.error('Failed to load travel forms:', e);
            setError('Failed to load travel forms. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleEditForm = (formId: number) => {
        navigate(`/travel-form?edit=${formId}`);
    };

    const handleDeleteForm = async (formId: number) => {
        if (!window.confirm('Are you sure you want to delete this travel form?')) return;

        try {
            const principal = await getClientPrincipal();
            if (!principal) {
                alert('Authentication required to delete form');
                return;
            }
            await deleteForm(formId, principal);
            setTravelForms(prev => prev.filter(form => form.id !== formId));
        } catch (e) {
            alert('Failed to delete travel form');
        }
    };

    // Duplicate feature removed - users can create new forms instead

    const formatDate = (dateStr: string) => {
        if (!dateStr) return 'N/A';
        return new Date(dateStr).toLocaleDateString();
    };

    // Calculate statistics
    const stats = {
        total: travelForms.length,
        draft: travelForms.filter(f => f.status === 'draft').length,
        submitted: travelForms.filter(f => f.status === 'submitted').length,
        approved: travelForms.filter(f => f.status === 'approved').length,
    };

    if (loading) {
        return (
            <Container maxWidth="lg">
                <Box sx={{ py: 8, textAlign: 'center' }}>
                    <CircularProgress />
                    <Typography sx={{ mt: 2 }}>Loading travel forms...</Typography>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg">
            <Box sx={{ py: 3 }}>
                {/* Header */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <FlightIcon color="primary" sx={{ fontSize: 32 }} />
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="h4" component="h1" gutterBottom>
                                Travel Forms
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Manage your travel requests and view approval status
                            </Typography>
                        </Box>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => navigate('/travel-form?new=true')}
                        >
                            New Travel Form
                        </Button>
                    </Stack>
                </Paper>

                {/* Error Alert */}
                {error && (
                    <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {/* Statistics Cards */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Typography color="text.secondary" gutterBottom>
                                    Total Forms
                                </Typography>
                                <Typography variant="h4">{stats.total}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Typography color="text.secondary" gutterBottom>
                                    Drafts
                                </Typography>
                                <Typography variant="h4" color="warning.main">{stats.draft}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Typography color="text.secondary" gutterBottom>
                                    Submitted
                                </Typography>
                                <Typography variant="h4" color="info.main">{stats.submitted}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Typography color="text.secondary" gutterBottom>
                                    Approved
                                </Typography>
                                <Typography variant="h4" color="success.main">{stats.approved}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Travel Forms Table */}
                <Paper>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Details</TableCell>
                                    <TableCell>Travel Dates</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {travelForms.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center">
                                            <Box sx={{ py: 4 }}>
                                                <Typography color="text.secondary">
                                                    No travel forms found. Create your first travel form to get started.
                                                </Typography>
                                                <Button
                                                    variant="contained"
                                                    startIcon={<AddIcon />}
                                                    onClick={() => navigate('/travel-form?new=true')}
                                                    sx={{ mt: 2 }}
                                                >
                                                    Create Travel Form
                                                </Button>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    travelForms.map((form) => (
                                        <TableRow key={form.id} hover>
                                            <TableCell>
                                                <Chip 
                                                    label={form.travel_type === 'travel' ? 'Travel' : 'Project'}
                                                    size="small"
                                                    color={form.travel_type === 'travel' ? 'secondary' : 'primary'}
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {form.travel_type === 'travel' ? (
                                                    <Typography variant="body2">
                                                        {form.travel_reason || 'General Travel'}
                                                    </Typography>
                                                ) : (
                                                    <>
                                                        <Typography variant="body2" fontWeight="medium">
                                                            {form.project_number}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {form.client || form.project_name || ''}
                                                        </Typography>
                                                    </>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {formatDate(form.travel_date_from)} - {formatDate(form.travel_date_to)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={form.status.toUpperCase()}
                                                    color={getStatusColor(form.status) as any}
                                                    size="small"
                                                    icon={getStatusIcon(form.status)}
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <Tooltip title="Edit">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleEditForm(form.id!)}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleDeleteForm(form.id!)}
                                                        color="error"
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Box>
        </Container>
    );
};

export default TravelListPage;
